import { lazy, Suspense, useCallback, useEffect, useState } from 'react'
import { fetchPublishedMenu } from './config/liveMenu'
import ClosedStoreState from './store/ClosedStoreState'
import { usePublicStoreStatus } from './store/usePublicStoreStatus'
import { fetchPricingDelivery } from './config/livePricingDelivery'
import { formatPublishedMenuDateRange } from './config/publishedMenuWeek'
import { homepageV3, type HomepageV3Product } from './config/homepageV3'
import type { PublicReview } from './components/ReviewCard'
import type { HomepageRuntimeContent, HomepageRuntimeMenu, HomepageRuntimePricing } from './HomepageV3'

const HomepageV3 = lazy(() => import('./HomepageV3'))
const DEFAULT_CONTENT: HomepageRuntimeContent & { weeklyMenuSupportingCopy: string } = {
  heroHeadingLineOne: homepageV3.top.headingLineOne,
  heroHeadingLineTwo: homepageV3.top.headingLineTwo,
  heroSupportingCopy: homepageV3.top.supportingCopy,
  weeklyMenuSupportingCopy: homepageV3.weeklyMenu.supportingCopy,
}
const LOADING_MENU: HomepageRuntimeMenu = {
  supportingCopy: "Loading this week's menu...",
  products: [],
}
const LOADING_PRICING: HomepageRuntimePricing = { prefix: '', price: 'Loading…' }

function asHomepageProduct(product: Awaited<ReturnType<typeof fetchPublishedMenu>>['products'][number]): HomepageV3Product & { imageAlt?: string } {
  return {
    id: product.id,
    name: product.name,
    type: product.type,
    description: product.description,
    image: product.image,
    imageAlt: product.imageAlt,
    framing: product.framing,
    availability: product.availability,
    enabled: true,
    displayOrder: product.displayOrder,
    isNew: false,
    clickTarget: '/order',
    hoverBackground: product.hoverBackground,
  }
}

function formatMoney(cents: number) {
  return new Intl.NumberFormat('en-NZ', {
    style: 'currency',
    currency: 'NZD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100)
}

function validContentField(value: unknown, fallback: string) {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback
}

export default function HomepageRuntimePage() {
  const [weeklyMenu, setWeeklyMenu] = useState<HomepageRuntimeMenu>(LOADING_MENU)
  const [publishedDateRange, setPublishedDateRange] = useState<string | null>(null)
  const [runtimePricing, setRuntimePricing] = useState<HomepageRuntimePricing>(LOADING_PRICING)
  const [runtimeContent, setRuntimeContent] = useState(DEFAULT_CONTENT)
  const [featuredReviews, setFeaturedReviews] = useState<PublicReview[]>([])
  const storeStatus = usePublicStoreStatus()

  const refreshMenu = useCallback(async () => {
    try {
      const menu = await fetchPublishedMenu()
      const dateRange = formatPublishedMenuDateRange(menu.week)
      if (!dateRange) throw new Error('published-menu-week-invalid')
      const products = menu.products.map(asHomepageProduct)
      setPublishedDateRange(dateRange)
      setWeeklyMenu({
        products,
        supportingCopy: products.length
          ? DEFAULT_CONTENT.weeklyMenuSupportingCopy
          : 'No flavours are available right now. Please check back later for the next published flavour drop.',
      })
    } catch {
      setPublishedDateRange(null)
      setWeeklyMenu({
        products: [],
        supportingCopy: "This week's menu is temporarily unavailable. Please check back shortly.",
      })
    }
  }, [])

  const refreshPricing = useCallback(async () => {
    try {
      const pricing = await fetchPricingDelivery()
      const lowestFee = Math.min(...pricing.delivery.bands.map((band) => band.feeCents))
      setRuntimePricing({ prefix: 'from', price: formatMoney(lowestFee) })
    } catch {
      setRuntimePricing({ prefix: '', price: 'Pricing unavailable' })
    }
  }, [])

  const refreshContent = useCallback(async () => {
    try {
      const response = await fetch('/api/content/homepage', { credentials: 'same-origin', cache: 'no-store' })
      if (!response.ok) return
      const payload = await response.json() as { fields?: Record<string, unknown> }
      const fields = payload.fields ?? {}
      setRuntimeContent({
        heroHeadingLineOne: validContentField(fields.heroHeadingLineOne, DEFAULT_CONTENT.heroHeadingLineOne),
        heroHeadingLineTwo: validContentField(fields.heroHeadingLineTwo, DEFAULT_CONTENT.heroHeadingLineTwo),
        heroSupportingCopy: validContentField(fields.heroSupportingCopy, DEFAULT_CONTENT.heroSupportingCopy),
        weeklyMenuSupportingCopy: validContentField(fields.weeklyMenuSupportingCopy, DEFAULT_CONTENT.weeklyMenuSupportingCopy),
      })
    } catch {
      // The approved source copy remains the safe fallback when managed content is unavailable.
    }
  }, [])

  const refreshFeaturedReviews = useCallback(async () => {
    try {
      const response = await fetch('/api/reviews/featured', { credentials: 'same-origin', cache: 'no-store' })
      if (!response.ok) throw new Error('featured-reviews-unavailable')
      const payload = await response.json() as { reviews?: PublicReview[] }
      const reviews = Array.isArray(payload.reviews) ? payload.reviews : []
      setFeaturedReviews(reviews.length === 3 ? reviews : [])
    } catch {
      setFeaturedReviews([])
    }
  }, [])

  useEffect(() => {
    void refreshMenu()
    void refreshPricing()
    void refreshContent()
    const timer = window.setInterval(() => {
      void refreshMenu()
      void refreshPricing()
      void refreshContent()
    }, 30_000)
    const onFocus = () => {
      void refreshMenu()
      void refreshPricing()
      void refreshContent()
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refreshMenu()
        void refreshPricing()
        void refreshContent()
      }
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [refreshContent, refreshMenu, refreshPricing])

  useEffect(() => {
    void refreshFeaturedReviews()
    const timer = window.setInterval(() => void refreshFeaturedReviews(), 30_000)
    const onFocus = () => void refreshFeaturedReviews()
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refreshFeaturedReviews()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [refreshFeaturedReviews])

  if (storeStatus.state !== 'open') {
    return (
      <ClosedStoreState
        variant="homepage"
        mode={storeStatus.state}
        status={storeStatus.status}
      />
    )
  }

  const renderedMenu: HomepageRuntimeMenu = weeklyMenu.products.length
    ? { ...weeklyMenu, supportingCopy: runtimeContent.weeklyMenuSupportingCopy }
    : weeklyMenu

  return (
    <Suspense fallback={null}>
      <HomepageV3
        weeklyMenu={renderedMenu}
        runtimePricing={runtimePricing}
        runtimeContent={runtimeContent}
        featuredReviews={featuredReviews}
        dateRange={publishedDateRange}
      />
    </Suspense>
  )
}
